import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // Check password complexity (backend requires: uppercase, lowercase, number, special char)
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(formData.password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined,
      });
      
      // Redirect to email verification page
      navigate('/verify-email', { 
        replace: true,
        state: { email: formData.email }
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign Up - Book My Yoga TTC"
        description="Create your account to book consultations and access exclusive yoga teacher training resources."
        url="https://bookmyyogattc.com/signup"
      />
      <div className="pt-20 min-h-screen flex">
        {/* Left Side - Visual */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary-dark to-primary relative overflow-hidden">
          {/* Background Image Overlay */}
          <div className="absolute inset-0">
            <img 
              src="/images/cover.png" 
              alt="Yoga" 
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary-dark/80 to-primary/90"></div>
          </div>
          
          {/* Pattern Overlay */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="yogaPatternSignup" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="white" strokeWidth="1"/>
                  <circle cx="60" cy="60" r="35" fill="none" stroke="white" strokeWidth="0.5"/>
                  <circle cx="60" cy="60" r="20" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#yogaPatternSignup)" />
            </svg>
          </div>
          
          <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-md"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity }}
                className="text-5 mb-6"
              >
                🧘
              </motion.div>
              <h2 className="text-5 font-semibold mb-4">Start Your Journey</h2>
              <p className="text-4 opacity-95 leading-relaxed">
                Join thousands of students who have transformed their lives through authentic yoga teacher training. Your path to becoming a certified instructor starts here.
              </p>
              
              {/* Features List */}
              <div className="mt-8 space-y-3 text-left">
                <div className="flex items-center space-x-3">
                  <span className="text-5">✓</span>
                  <span className="opacity-90">Free consultation with experts</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-5">✓</span>
                  <span className="opacity-90">Access to verified yoga schools</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-5">✓</span>
                  <span className="opacity-90">Personalized recommendations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-5">✓</span>
                  <span className="opacity-90">Application tracking & support</span>
                </div>
              </div>
            </motion.div>
            
            {/* Floating Elements */}
            <motion.div
              animate={{ y: [0, -30, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity }}
              className="absolute top-20 left-20 text-5 opacity-15"
            >
              🕉️
            </motion.div>
            <motion.div
              animate={{ y: [0, 30, 0], rotate: [0, -10, 0] }}
              transition={{ duration: 7, repeat: Infinity, delay: 1 }}
              className="absolute bottom-20 right-20 text-5 opacity-15"
            >
              🪷
            </motion.div>
            <motion.div
              animate={{ y: [0, -25, 0] }}
              transition={{ duration: 5, repeat: Infinity, delay: 0.5 }}
              className="absolute top-1/2 right-10 text-5 opacity-10"
            >
              ☯️
            </motion.div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-6 lg:p-12">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-lg"
          >
            <div className="mb-6">
              <h1 className="text-5 font-semibold text-ivory-900 mb-2">Create Account</h1>
              <p className="text-ivory-600">Join us and start your yoga journey</p>
            </div>

            <div className="bg-white rounded-xl shadow-xl p-8 border border-ivory-100">
              <form
                className="space-y-5"
                onSubmit={handleSubmit}
              >
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-2">
                    {error}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-ivory-700 font-medium mb-2">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-ivory-700 font-medium mb-2">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-ivory-700 font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-ivory-700 font-medium mb-2">
                    Phone Number <span className="text-ivory-500 font-normal text-2">(Optional)</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-ivory-700 font-medium mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-12 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                      placeholder="At least 6 characters"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center hover:opacity-70 transition-opacity"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-ivory-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-ivory-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-ivory-700 font-medium mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-12 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center hover:opacity-70 transition-opacity"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-ivory-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-ivory-400" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-6 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Create Account'
                  )}
                </button>

                <div className="text-center pt-4 border-t border-ivory-200">
                  <p className="text-2 text-ivory-600">
                    Already have an account?{' '}
                    <Link to="/login" className="font-semibold text-primary hover:text-primary-dark">
                      Sign in
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Signup;

